import { useState, useEffect, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { BASE_URL } from '../apiConfig'
import { useAuth } from '../context/AuthProvider'
import { useLanguage } from '../context/LanguageProvider'
import { ChevronLeft, ChevronRight, Tag, ArrowRight , ArrowLeft } from 'lucide-react'

export default function DiscountBanner() {
  const { token } = useAuth()
  const { t, locale } = useLanguage()
  const [products, setProducts] = useState([])
  const [loading, setLoading] = useState(true)
  const [current, setCurrent] = useState(0)
  const [isPaused, setIsPaused] = useState(false)
  const [direction, setDirection] = useState(0)
  const [isAnimating, setIsAnimating] = useState(false)

  useEffect(() => {
    fetch(`${BASE_URL}/api/get-discounted-products`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.status && data.data) {
          setProducts(data.data)
        }
        setLoading(false)
      })
      .catch(() => setLoading(false))
  }, [token])

  const next = useCallback(() => {
    if (isAnimating) return
    setDirection(1)
    setIsAnimating(true)
    setCurrent((prev) => (prev + 1) % products.length)
    setTimeout(() => setIsAnimating(false), 400)
  }, [products.length, isAnimating])

  const prev = useCallback(() => {
    if (isAnimating) return
    setDirection(-1)
    setIsAnimating(true)
    setCurrent((prev) => (prev - 1 + products.length) % products.length)
    setTimeout(() => setIsAnimating(false), 400)
  }, [products.length, isAnimating])

  useEffect(() => {
    if (loading || isPaused || products.length <= 1) return
    const timer = setInterval(next, 4000)
    return () => clearInterval(timer)
  }, [loading, isPaused, products.length, next])

  if (loading || products.length === 0) return null

  const product = products[current]
  const originalPrice = parseFloat(product.price?.saller_price || 0)
  const discountValue = product.discount?.value || 0
  const discountedPrice = originalPrice - (originalPrice * discountValue) / 100

  const formatPrice = (price) =>
    new Intl.NumberFormat('fr', { style: 'currency', currency: 'MAD' }).format(price)

  const imageUrl = product.image_py ? `${BASE_URL}/storage/${product.image_py}` : null

  // Slide animation variants
  const getSlideAnimation = () => {
    if (!isAnimating) return 'translate-x-0 opacity-100'
    if (direction === 1) {
      return 'translate-x-[30px] opacity-0'
    } else {
      return '-translate-x-[30px] opacity-0'
    }
  }

  return (
    <div
      className="relative w-full rounded-3xl overflow-hidden mb-4 min-h-[240px] md:min-h-[260px]"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      {/* Diagonal background */}
      <div
        className="absolute inset-0"
        style={{
          background:
            'linear-gradient(120deg, #0a3a36 0%, #0f4a45 42%, #1c6b5e 68%, #35937c 100%)',
        }}
      />

      {/* Scattered decorative outlines — kept faint, never competes with the product */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <span className="absolute top-6 left-16 w-3 h-3 border border-white/25 rotate-45 hidden sm:block" />
        <span className="absolute top-24 left-8 w-2 h-2 border border-white/20 rotate-45 hidden sm:block" />
        <span className="absolute bottom-10 left-28 w-2.5 h-2.5 border border-white/20 rotate-45 hidden sm:block" />
        <span className="absolute top-10 right-[38%] w-2 h-2 border border-white/25 rotate-45 hidden md:block" />
        <span className="absolute bottom-16 right-[8%] w-2.5 h-2.5 border border-emerald-300/40 rotate-45 hidden md:block" />
        <svg className="absolute top-4 left-6 w-6 h-6 text-white/20 hidden sm:block" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <path d="M6 3v6M3 6h6M17 13l1.8 4.2L23 19l-4.2 1.8L17 25l-1.8-4.2L11 19l4.2-1.8L17 13z" />
        </svg>
      </div>

      {/* Content with slide animation */}
      <div 
        className={`relative z-10 flex flex-col md:flex-row items-center justify-between gap-6 md:gap-4 px-2 py-4 md:px-6 ${locale == "ar" ? "lg:pr-6 lg:pl-24" : "lg:pl-6 lg:pr-24"}  h-full transition-all duration-300 ease-in-out ${getSlideAnimation()}`}
      >
        {/* Text column */}
        <div className={`${locale === "ar" ? "md:text-right" : "md:text-left"} flex-1 min-w-0 text-center max-w-md order-1`}>
          <span className="inline-flex items-center gap-1.5 border border-dashed border-[#f4c453]/70 bg-[#f4c453]/10 text-[#f4c453] text-[10.5px] font-bold uppercase tracking-[0.14em] px-3 py-1 rounded-full mb-1">
            <Tag className="w-3 h-3" />
            {t('products.special_offer') || 'Offre spéciale'}
          </span>

          <h2 className="text-xl md:text-2xl font-extrabold text-white/80  mb-1">
            {t('products.super_discounts') || 'Super réductions'}
            <br />
            <p className="text-white text-4xl  mb-2 max-w-xs mx-auto md:mx-0">
              {product.name}
            </p>
          </h2>

          <p className="font-medium text-white/85 mb-5">
            {product.category?.title
              ? `sur ${product.category.title}`
              : t('products.on_selected_products') || 'sur une sélection de produits'}
            {' !'}
          </p>

          <Link
            to={`/products/${product.id}`}
            className="inline-flex items-center gap-2 bg-white text-[#0f4a45] font-bold text-xs md:text-sm px-5 py-2.5 md:px-6 md:py-3 rounded-full hover:bg-white/90 transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-[1.03] active:scale-95"
          >
            {t('products.discover_offers') || 'Découvrir les offres'}
            {
              locale == "ar" ?
              <ArrowLeft className="w-3.5 h-3.5" />
              :
              <ArrowRight className="w-3.5 h-3.5" />
            }
          </Link>
        </div>

        {/* Circular discount badge */}
        {discountValue > 0 && (
          <div className="order-2 flex-shrink-0 relative w-28 h-28 md:w-36 md:h-36 lg:w-40 lg:h-40 rounded-full flex items-center justify-center">
            <div className="absolute inset-0 rounded-full border-2 border-white/25" />
            <div className="absolute inset-[10px] rounded-full bg-[#fbfdfc] shadow-[0_10px_28px_-8px_rgba(0,0,0,0.4)] flex flex-col items-center justify-center text-center px-2">
              <span className="text-[10px] md:text-[11px] font-bold text-[#0f4a45]/70 tracking-wide">
                {t('products.up_to') || "JUSQU'À"}
              </span>
              <span className="text-2xl md:text-3xl lg:text-5xl font-black text-[#0f4a45] leading-none my-0.5">
                {locale === "ar" ? <>{discountValue}%-</> : <>-{discountValue}%</> } 
              </span>
              <span className="text-[9px] md:text-[10px] font-bold text-[#0f4a45]/70 tracking-wide uppercase">
                {t('products.discount') || 'de réduction'}
              </span>
            </div>
          </div>
        )}

        {/* Product on pedestal */}
        <div className="order-3 flex-shrink-0 relative w-36 h-36 md:w-52 md:h-52 lg:w-60 lg:h-60 flex items-end justify-center">
          {/* pedestal */}
          <div className="absolute bottom-2 md:bottom-4 w-[85%] h-6 md:h-9 rounded-[50%] bg-emerald-300/30 blur-[2px]" />

          {/* product image frame */}
          <div className="relative z-10 w-28 h-28 md:w-40 md:h-40 lg:w-44 lg:h-44 mb-4 md:mb-6 rounded-2xl overflow-hidden bg-white shadow-[0_16px_30px_-10px_rgba(0,0,0,0.45)] border-4 border-white/90">
            {imageUrl ? (
              <img src={imageUrl} alt={product.name} className="w-full h-full object-cover" key={product.id} />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-emerald-50">
                <Tag className="w-10 h-10 text-[#0f4a45]/40" />
              </div>
            )}
          </div>

          {/* floating tag decoration - top right */}
          <div className="absolute top-0 right-0 md:-right-2 w-9 h-9 md:w-11 md:h-11 rounded-xl bg-emerald-400 flex items-center justify-center rotate-6 shadow-lg z-20">
            <span className="text-white font-black text-xs md:text-lg">%</span>
          </div>

          {/* floating tag decoration - bottom left, only if there IS a discount to show */}
          {discountValue > 0 && (
            <div className="absolute bottom-8 -left-2 md:-left-3 w-8 h-8 md:w-10 md:h-10 rounded-xl bg-amber-400 flex items-center justify-center -rotate-12 shadow-lg z-20">
              <span className="text-[#1c1200] font-black text-[10px] md:text-lg">%</span>
            </div>
          )}

          {/* price pill */}
          <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 whitespace-nowrap bg-[#0a3a36] text-white text-[10px] md:text-[11px] font-semibold px-3 py-1.5 rounded-full shadow-lg flex items-center gap-1.5 z-20">
            <span className="line-through opacity-50 font-medium">{formatPrice(originalPrice)}</span>
            <span className="text-emerald-300 font-bold">{formatPrice(discountedPrice)}</span>
          </div>
        </div>
      </div>

      {/* Nav arrows, bottom right per reference */}
      {products.length > 1 && (
        <div className={`${locale === "ar" ? "left-5" : "right-5"} absolute bottom-4 z-20 flex items-center gap-2.5`}>
          <button
            onClick={prev}
            disabled={isAnimating}
            aria-label={t('common.previous') || 'Précédent'}
            className="w-8 h-8 md:w-9 md:h-9 rounded-full bg-white text-[#0f4a45] flex items-center justify-center shadow-md hover:bg-white/90 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[#0f4a45]"
          >
            {
              locale == "ar" ?
              <ChevronRight className="w-4 h-4 md:w-5 md:h-5" />
              :
              <ChevronLeft className="w-4 h-4 md:w-5 md:h-5" />
            }
          </button>
          <button
            onClick={next}
            disabled={isAnimating}
            aria-label={t('common.next') || 'Suivant'}
            className="w-8 h-8 md:w-9 md:h-9 rounded-full bg-white text-[#0f4a45] flex items-center justify-center shadow-md hover:bg-white/90 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[#0f4a45]"
          >
            {
              locale == "ar" ?
              <ChevronLeft className="w-4 h-4 md:w-5 md:h-5" />
              :
              <ChevronRight className="w-4 h-4 md:w-5 md:h-5" />
            }
          </button>
        </div>
      )}

      {/* Dot indicators, bottom center */}
      {products.length > 1 && (
        <div className="absolute bottom-5 left-1/2 -translate-x-1/2 z-20 flex gap-1.5">
          {products.map((_, i) => (
            <button
              key={i}
              onClick={() => {
                if (isAnimating || i === current) return
                setDirection(i > current ? 1 : -1)
                setIsAnimating(true)
                setCurrent(i)
                setTimeout(() => setIsAnimating(false), 400)
              }}
              disabled={isAnimating}
              aria-label={`${t('common.slide') || 'Diapositive'} ${i + 1}`}
              className={`h-1.5 rounded-full transition-all duration-300 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed ${
                i === current ? 'bg-white w-5' : 'bg-white/35 hover:bg-white/55 w-1.5'
              }`}
            />
          ))}
        </div>
      )}
    </div>
  )
}